import axios from 'axios';

// Auto-detect API URL based on environment
const API_URL = process.env.REACT_APP_API_URL || (
    process.env.NODE_ENV === 'production'
        ? '/api'  // Use relative path (IIS will proxy to backend via web.config)
        : 'http://localhost:5000/api'  // For local development
);

const api = axios.create({
    baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    getProfile: () => api.get('/auth/me'),
};

// Products API
export const productsAPI = {
    getAll: (params) => api.get('/products', { params }),
    getById: (id) => api.get(`/products/${id}`),
    create: (data) => api.post('/products', data),
    update: (id, data) => api.put(`/products/${id}`, data),
    delete: (id) => api.delete(`/products/${id}`),
    getLowStock: () => api.get('/products/alerts/low-stock'),
};

// Sales API
export const salesAPI = {
    getAll: (params) => api.get('/sales', { params }),
    getById: (id) => api.get(`/sales/${id}`),
    create: (data) => api.post('/sales', data),
    getReport: (params) => api.get('/sales/reports/summary', { params }),
    cancel: (id, data) => api.post(`/sales/${id}/cancel`, data),
};

// Promotions API
export const promotionsAPI = {
    getAll: (params) => api.get('/promotions', { params }),
    getActive: () => api.get('/promotions/active'),
    getById: (id) => api.get(`/promotions/${id}`),
    create: (data) => api.post('/promotions', data),
    update: (id, data) => api.put(`/promotions/${id}`, data),
    delete: (id) => api.delete(`/promotions/${id}`),
    uploadImage: (id, formData) => api.post(`/promotions/${id}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    calculateDiscount: (data) => api.post('/promotions/calculate', data),
    calculateCartDiscount: (data) => api.post('/promotions/calculate-cart', data),
};

// Users API
export const usersAPI = {
    getAll: () => api.get('/users'),
    getById: (id) => api.get(`/users/${id}`),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    changePassword: (id, data) => api.put(`/users/${id}/password`, data),
};

// Roles API
export const rolesAPI = {
    getAll: () => api.get('/roles'),
    getById: (id) => api.get(`/roles/${id}`),
    create: (data) => api.post('/roles', data),
    update: (id, data) => api.put(`/roles/${id}`, data),
    delete: (id) => api.delete(`/roles/${id}`),
    getPermissions: () => api.get('/roles/permissions/list'),
};

// Categories API
export const categoriesAPI = {
    getAll: () => api.get('/categories'),
    create: (data) => api.post('/categories', data),
    update: (id, data) => api.put(`/categories/${id}`, data),
    delete: (id) => api.delete(`/categories/${id}`),
};

// Settings API
export const settingsAPI = {
    get: () => api.get('/settings'),
    update: (data) => api.put('/settings', data),
    uploadQRCode: (formData) => api.post('/settings/qr-code', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    uploadLogo: (formData) => api.post('/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deleteSales: () => api.delete('/settings/delete-sales'),
    resetStock: () => api.post('/settings/reset-stock'),
    deleteProducts: () => api.delete('/settings/delete-products'),
    deletePromotions: () => api.delete('/settings/delete-promotions'),
    deleteMembers: () => api.delete('/settings/delete-members'),
    deleteStockCounts: () => api.delete('/settings/delete-stock-counts'),
    deleteStockMovements: () => api.delete('/settings/delete-stock-movements'),
};

// Members API
export const membersAPI = {
    getAll: (params) => api.get('/members', { params }),
    getById: (id) => api.get(`/members/${id}`),
    searchByPhone: (phone) => api.get(`/members/search/phone/${phone}`),
    create: (data) => api.post('/members', data),
    update: (id, data) => api.put(`/members/${id}`, data),
    delete: (id) => api.delete(`/members/${id}`),
    updatePoints: (id, data) => api.post(`/members/${id}/points`, data),
};

// Stock Counts API
export const stockCountsAPI = {
    getAll: (params) => api.get('/stock-counts', { params }),
    getById: (id) => api.get(`/stock-counts/${id}`),
    create: (data) => api.post('/stock-counts', data),
    updateItem: (countId, itemId, data) => api.put(`/stock-counts/${countId}/items/${itemId}`, data),
    complete: (id) => api.post(`/stock-counts/${id}/complete`),
    delete: (id) => api.delete(`/stock-counts/${id}`),
};

export default api;
